import { Button } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import { Modal } from '@thanx/uikit/modal'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import Checkmark from 'components/Icons/Checkmark'
import ExternalLink from 'components/Icons/ExternalLink'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { oloDashboardUrl, oloWebhookSetupGuideUrl } from 'utilities/urlUtils'
import { withMarkers } from './withMarkers'

const t = buildTranslate('live_updates.olo_webhook_modal')

const MODAL_Z_INDEX = 1200
// baseweb hardcodes aria-label="dialog" on the Dialog element and does NOT
// forward a top-level aria-label/aria-labelledby to it, so the only way to
// name this dialog is overrides.Dialog.props (same approach as
// components/ModalContainer). Supplying a Dialog override replaces uikit's own
// `{ style: getDialogStyle(size) }` entry wholesale rather than merging into
// it, so the margin that style applies at this size is restated here.
const DIALOG_MARGIN = '3rem'
// Widened from 510px to match Figma 1354:33070 — the prior width read as
// noticeably narrower than the design once the URL/copy field was reworked
// below.
const CONTENT_WIDTH = '600px'
const STEP_BADGE_SIZE = '22px'
const STEP_GAP = '12px'
const SECTION_GAP = '24px'
const DIVIDER_WIDTH = '1px'
const COPY_BUTTON_PADDING = '16px'

// Marker the step copy interpolates in place of the fragment that has to
// render as its own element (a link, a bolded field name) — splitting the
// translated sentence on it keeps the copy one string per step rather than
// three fragments a translator has to reassemble. Exported (with withInline
// below) so the throw path can be unit-tested directly against a malformed
// string, without reaching into i18n internals to break a real locale key.
export const INLINE_MARKER = '[[inline]]'

// The URL section's three steps are spelled out individually (two of them
// embed an element mid-sentence); the secret section's are uniform enough to
// map. Deriving the secret section's numbering from this array's length
// (rather than a separately-maintained count) keeps the two sections from
// silently desyncing if a step is ever added or removed from either one.
const URL_STEP_KEYS = ['one', 'two', 'three'] as const
const SECRET_STEP_KEYS = ['four', 'five', 'six'] as const
const TOTAL_STEP_COUNT = URL_STEP_KEYS.length + SECRET_STEP_KEYS.length

export interface OloWebhookModalProps {
  isOpen: boolean
  onClose: () => void
  webhookUrl: string
  onSave: (sharedSecret: string) => Promise<void>
  isSaving: boolean
  saveError: string | null
}

// The single-marker case of withMarkers (kept as its own named export since
// every call site here only ever splices in one fragment).
export const withInline = (copy: string, node: React.ReactNode) =>
  withMarkers(copy, [[INLINE_MARKER, node]])

const inlineLink = (href: string, label: string) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
)

type StepProps = {
  number: number
  children: React.ReactNode
}

const Step = ({ number, children }: StepProps) => {
  const [css] = useStyletron()

  return (
    <li
      // The URL and secret sections render as two separate <ol> elements
      // (Section splits them, above), so each one's native list position
      // resets to 1 — without these, a screen reader announces the secret
      // section's first item as "item 1 of 3" while its visible badge reads
      // "4". Explicit posinset/setsize restate the true position across both
      // lists combined so the announced number matches the badge.
      aria-posinset={number}
      aria-setsize={TOTAL_STEP_COUNT}
      className={css({ display: 'flex', gap: STEP_GAP })}
    >
      {/* Decorative: the surrounding <ol> already conveys the ordering, so the
          digit stays out of the accessible name to avoid it being read twice. */}
      <span
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: STEP_BADGE_SIZE,
          height: STEP_BADGE_SIZE,
          borderRadius: '50%',
          backgroundColor: palette.belizeHole,
        })}
      >
        <Text.BodyText5 bold color="white">
          {number}
        </Text.BodyText5>
      </span>
      <Text.BodyText4 color="grey90">{children}</Text.BodyText4>
    </li>
  )
}

type SectionProps = {
  label: string
  children: React.ReactNode
}

const Section = ({ label, children }: SectionProps) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        marginTop: SECTION_GAP,
        display: 'flex',
        flexDirection: 'column',
        gap: STEP_GAP,
      })}
    >
      <Text.BodyText5
        tag="h4"
        bold
        color="grey70"
        className={css({
          display: 'block',
          margin: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        })}
      >
        {label}
      </Text.BodyText5>
      {children}
    </div>
  )
}

const StepList = ({ children }: { children: React.ReactNode }) => {
  const [css] = useStyletron()

  return (
    <ol
      className={css({
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: STEP_GAP,
      })}
    >
      {children}
    </ol>
  )
}

// "Connect Olo webhooks" setup/rotate dialog (Figma 1354:33049). Doubles as
// the first-time setup flow and the later "rotate the secret" flow — both
// just call the same PUT olo_webhook_credentials; there's no separate
// verification handshake (7/21 Slack decision: configure it, save it, and let
// the connection status update on its own as receipts arrive).
const OloWebhookModal = ({
  isOpen,
  onClose,
  webhookUrl,
  onSave,
  isSaving,
  saveError,
}: OloWebhookModalProps) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [sharedSecret, setSharedSecret] = useState('')
  const [isSaved, setIsSaved] = useState(false)
  const savedRef = useRef<HTMLDivElement>(null)

  // Reset the draft secret and the success view whenever the dialog (re)opens
  // so a previous attempt never survives into a fresh open.
  useEffect(() => {
    if (isOpen) {
      setSharedSecret('')
      setIsSaved(false)
    }
  }, [isOpen])

  // The save swaps the entire dialog body from the setup steps to this
  // confirmation — nothing else moves focus, so without this a screen reader
  // user's focus reverts to <body> with no cue the save succeeded or where
  // they landed. tabIndex (below) makes the otherwise-inert container a
  // valid focus target; a uikit Text heading can't be trusted to forward a
  // ref, so the container itself — not the heading — is what's focused.
  useEffect(() => {
    if (isSaved) savedRef.current?.focus()
  }, [isSaved])

  const handleCopyUrl = async () => {
    if (!webhookUrl) return
    try {
      await navigator.clipboard.writeText(webhookUrl)
      dispatch(alert({ key: 'success', message: t('url_copied'), timeout: 5 }))
    } catch {
      // navigator.clipboard is unavailable outside a secure context, and
      // writeText rejects on a denied permission or a missing user gesture —
      // surface a failure toast rather than leaving the click with no
      // feedback at all.
      dispatch(
        alert({ key: 'danger', message: t('url_copy_failed'), timeout: 5 })
      )
    }
  }

  const handleSubmit = async () => {
    if (!sharedSecret.trim() || isSaving) return
    try {
      await onSave(sharedSecret.trim())
      setIsSaved(true)
    } catch {
      // Surfaced via saveError below; onSave's caller keeps the modal open.
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSaving ? undefined : onClose}
      size="auto"
      closeType="small"
      zIndex={MODAL_Z_INDEX}
      overrides={{
        Dialog: {
          props: { 'aria-label': t('title') },
          style: () => ({
            marginTop: DIALOG_MARGIN,
            marginRight: DIALOG_MARGIN,
            marginBottom: DIALOG_MARGIN,
            marginLeft: DIALOG_MARGIN,
          }),
        },
      }}
    >
      <div
        className={`p-xl d-flex flex-column ${css({
          width: CONTENT_WIDTH,
          maxWidth: '100%',
        })}`}
      >
        <Text.Header3 tag="h3" className="m-0 text-center">
          {t('title')}
        </Text.Header3>

        {isSaved ? (
          // tabIndex + the focus effect above land keyboard/screen-reader
          // focus on this container since the Save button that was focused
          // no longer exists in the DOM once the confirmation replaces it.
          // No outline override: this is a real focus target (not a
          // decorative one), so the browser's default focus ring stays
          // visible per WCAG 2.4.7.
          <div ref={savedRef} tabIndex={-1} data-testid="olo-webhook-saved">
            {/* role="status" + aria-live scoped to just the announced text
                (mirrors the aria-live pattern StatusCard / MessageTextField
                use elsewhere in this scene) — deliberately excludes the Done
                button below, so its label isn't read as part of the
                announcement. */}
            <div role="status" aria-live="polite">
              <div
                className={css({
                  marginTop: SECTION_GAP,
                  display: 'flex',
                  alignItems: 'center',
                  gap: STEP_GAP,
                })}
              >
                {/* Decorative: the "Key saved" heading beside it carries the
                    meaning. */}
                <span
                  aria-hidden="true"
                  className={css({ display: 'flex', flexShrink: 0 })}
                >
                  <Checkmark />
                </span>
                <Text.BodyText3
                  tag="h4"
                  bold
                  color="grey90"
                  className={css({ display: 'block', margin: 0 })}
                >
                  {t('saved_title')}
                </Text.BodyText3>
              </div>
              <Text.BodyText4
                color="grey70"
                className={css({ display: 'block', marginTop: '8px' })}
              >
                {t('saved_body')}
              </Text.BodyText4>
            </div>
            {/* SECTION_GAP (24px), not the mt-l utility (32px) the Submit
                button below uses — 32px read as too much whitespace above
                this confirmation's Done button per design review. */}
            <Button
              className={`d-block mx-auto ${css({ marginTop: SECTION_GAP })}`}
              onClick={onClose}
            >
              {t('saved_confirm')}
            </Button>
          </div>
        ) : (
          <>
            <Text.BodyText4
              color="grey70"
              className={css({
                display: 'block',
                textAlign: 'center',
                marginTop: '8px',
              })}
            >
              {t('subtitle')}
            </Text.BodyText4>

            <Section label={t('url_section_label')}>
              <StepList>
                {URL_STEP_KEYS.map((key, index) => (
                  <Step key={key} number={index + 1}>
                    {key === 'one' &&
                      withInline(
                        t('steps.one', { inline: INLINE_MARKER }),
                        inlineLink(oloDashboardUrl, t('steps.one_inline'))
                      )}
                    {key === 'two' && t('steps.two')}
                    {key === 'three' &&
                      withInline(
                        t('steps.three', { inline: INLINE_MARKER }),
                        <Text.BodyText4 bold color="grey90">
                          {t('steps.three_inline')}
                        </Text.BodyText4>
                      )}
                  </Step>
                ))}
              </StepList>

              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                })}
              >
                <Text.BodyText4 bold color="grey90" className="m-0">
                  {t('webhook_url_label')}
                </Text.BodyText4>
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'stretch',
                    borderRadius: '4px',
                    border: `1px solid ${palette.grey30}`,
                    backgroundColor: palette.grey10,
                    overflow: 'hidden',
                  })}
                >
                  <div
                    className={css({
                      flex: 1,
                      padding: '12px',
                      overflowWrap: 'anywhere',
                    })}
                  >
                    {/* BodyText5 (12px), smaller than the surrounding
                        BodyText4 copy — matches Figma 1354:33070, which
                        keeps this long URL on close to one line. */}
                    <Text.BodyText5 color="grey70">{webhookUrl}</Text.BodyText5>
                  </div>
                  <div
                    aria-hidden="true"
                    className={css({
                      width: DIVIDER_WIDTH,
                      backgroundColor: palette.grey30,
                    })}
                  />
                  <Button
                    kind="minimal"
                    onClick={handleCopyUrl}
                    disabled={!webhookUrl}
                    className={css({
                      paddingLeft: COPY_BUTTON_PADDING,
                      paddingRight: COPY_BUTTON_PADDING,
                    })}
                  >
                    {t('copy_url')}
                  </Button>
                </div>
                <Text.BodyText5 color="grey70">
                  {t('webhook_url_note')}
                </Text.BodyText5>
              </div>
            </Section>

            <Section label={t('secret_section_label')}>
              <StepList>
                {SECRET_STEP_KEYS.map((key, index) => (
                  <Step key={key} number={URL_STEP_KEYS.length + index + 1}>
                    {t(`steps.${key}`)}
                  </Step>
                ))}
              </StepList>
            </Section>

            <a
              href={oloWebhookSetupGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-l ${css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontWeight: 700,
              })}`}
            >
              {t('setup_guide_link')}
              {/* Decorative: the label already says where the link goes. */}
              <ExternalLink />
            </a>

            <div
              className={css({
                marginTop: SECTION_GAP,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              })}
            >
              <label
                htmlFor="olo-webhook-secret"
                className={css({ margin: 0 })}
              >
                <Text.BodyText4 bold color="grey90">
                  {t('secret_label')}
                </Text.BodyText4>
              </label>
              <Input
                id="olo-webhook-secret"
                type="password"
                value={sharedSecret}
                placeholder={t('secret_placeholder')}
                disabled={isSaving}
                onChange={setSharedSecret}
              />
            </div>

            {saveError && (
              <div className="mt-m">
                <Notification kind="negative" size="full">
                  {saveError}
                </Notification>
              </div>
            )}

            <Button
              className="mt-l align-self-center"
              disabled={!sharedSecret.trim() || isSaving}
              isLoading={isSaving}
              onClick={handleSubmit}
            >
              {t('submit')}
            </Button>
          </>
        )}
      </div>
    </Modal>
  )
}

export default OloWebhookModal
